// CR036: Flyer Diagnostics Pipeline v2 Runner

import { supabase } from '@/integrations/supabase/client';
import { computeCandidateScore, computePrimaryOcrScore, DEFAULT_CANDIDATE_SCORING_CONFIG } from './candidateScoringEngine';
import type {
  CandidateScoringConfig,
  PrimaryOcrEngine,
  PrimaryOcrResult,
  CandidateScoringResult,
  AiScanResult,
  NormalizedDeal,
  ScanRunV2,
} from './types';

// ============================================================================
// PRIMARY OCR (TESSERACT)
// ============================================================================

export async function runPrimaryOcr(
  imageUrl: string,
  engine: PrimaryOcrEngine
): Promise<PrimaryOcrResult> {
  const startTime = Date.now();
  
  if (engine === 'none') {
    return {
      engine: 'none',
      text: '',
      confidence: 0,
      duration_ms: 0,
      score_total: 0,
      score_breakdown: {
        alphanumeric_ratio: 0,
        currency_pattern_count: 0,
        symbol_noise_ratio: 0,
        text_length: 0,
        word_count: 0
      }
    };
  }
  
  try {
    // Import Tesseract dynamically
    const Tesseract = await import('tesseract.js');
    const worker = await Tesseract.createWorker('eng');
    
    console.log(`[CR036] Running Tesseract OCR on: ${imageUrl.substring(0, 50)}...`);
    
    const result = await worker.recognize(imageUrl);
    await worker.terminate();
    
    const text = result.data.text || '';
    const confidence = result.data.confidence || 0;
    const durationMs = Date.now() - startTime;
    
    // Compute OCR quality score
    const scoreResult = computePrimaryOcrScore(text);
    
    console.log(`[CR036] Tesseract complete: ${text.length} chars, confidence=${confidence}%, score=${scoreResult.total}`);
    
    return {
      engine: 'tesseract',
      text,
      confidence,
      duration_ms: durationMs,
      score_total: scoreResult.total,
      score_breakdown: {
        alphanumeric_ratio: scoreResult.breakdown.alphanumeric_ratio,
        currency_pattern_count: scoreResult.breakdown.currency_pattern_count,
        symbol_noise_ratio: scoreResult.breakdown.symbol_noise_ratio,
        text_length: scoreResult.breakdown.text_length,
        word_count: scoreResult.breakdown.word_count
      }
    };
  } catch (error) {
    console.error(`[CR036] Primary OCR error:`, error);
    return {
      engine,
      text: '',
      confidence: 0,
      duration_ms: Date.now() - startTime,
      score_total: 0,
      score_breakdown: {
        alphanumeric_ratio: 0,
        currency_pattern_count: 0,
        symbol_noise_ratio: 0,
        text_length: 0,
        word_count: 0
      },
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// ============================================================================
// CANDIDATE SCORING
// ============================================================================

export async function evaluateCandidateScoring(
  ocrText: string,
  primaryOcrScore: number,
  configId?: string
): Promise<CandidateScoringResult> {
  // Load config from database or use default
  let config: CandidateScoringConfig = DEFAULT_CANDIDATE_SCORING_CONFIG;
  
  if (configId) {
    const { data, error } = await supabase
      .from('flyer_candidate_scoring_config')
      .select('*')
      .eq('id', configId)
      .eq('is_active', true)
      .single();
    
    if (!error && data) {
      config = {
        version: data.version,
        enabled: data.enabled,
        threshold: Number(data.threshold),
        min_primary_ocr_score: Number(data.min_primary_ocr_score),
        signals: (Array.isArray(data.signals) ? data.signals : []) as unknown as CandidateScoringConfig['signals']
      };
    }
  } else {
    // Try to get the active config
    const { data } = await supabase
      .from('flyer_candidate_scoring_config')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    
    if (data) {
      config = {
        version: data.version,
        enabled: data.enabled,
        threshold: Number(data.threshold),
        min_primary_ocr_score: Number(data.min_primary_ocr_score),
        signals: (Array.isArray(data.signals) ? data.signals : []) as unknown as CandidateScoringConfig['signals']
      };
    }
  }
  
  return computeCandidateScore(ocrText, primaryOcrScore, config);
}

// ============================================================================
// AI PAGE SCAN
// ============================================================================

export async function runAiPageScan(
  imageUrl: string,
  pageNumber: number,
  merchantName: string,
  ocrContext?: string,
  aiModel?: string
): Promise<AiScanResult> {
  const startTime = Date.now();
  const modelToUse = aiModel || 'google/gemini-3-flash-preview';
  
  try {
    console.log(`[CR036] Starting AI page scan for page ${pageNumber} using ${modelToUse}`);
    
    const { data, error } = await supabase.functions.invoke('ai-page-scan', {
      body: {
        image_url: imageUrl,
        page_number: pageNumber,
        merchant_name: merchantName,
        ocr_context: ocrContext,
        model: modelToUse
      }
    });
    
    if (error) {
      throw new Error(`AI scan function error: ${error.message}`);
    }
    
    const durationMs = Date.now() - startTime;
    
    if (!data.success) {
      return {
        status: 'failed',
        deals: [],
        raw_output: data,
        duration_ms: durationMs,
        model_used: data.model_used || 'google/gemini-3-flash-preview',
        error: data.error || 'AI scan failed'
      };
    }
    
    console.log(`[CR036] AI scan complete: ${data.deals?.length || 0} deals found`);
    
    return {
      status: 'complete',
      deals: data.deals || [],
      raw_output: data,
      duration_ms: durationMs,
      model_used: data.model_used || 'google/gemini-3-flash-preview'
    };
  } catch (error) {
    console.error(`[CR036] AI page scan error:`, error);
    return {
      status: 'failed',
      deals: [],
      duration_ms: Date.now() - startTime,
      model_used: 'google/gemini-3-flash-preview',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// ============================================================================
// NORMALIZE DEALS
// ============================================================================

export function normalizeAiDeals(
  aiResult: AiScanResult,
  runId: string,
  pageNumber: number,
  primaryScore: number,
  candidateScore: number,
  candidateReasonCodes: string[]
): NormalizedDeal[] {
  if (aiResult.status !== 'complete' || !aiResult.deals.length) {
    return [];
  }
  
  return aiResult.deals.map((deal, index) => ({
    deal_id: `${runId}-${pageNumber}-${index}`,
    run_id: runId,
    page_no: pageNumber,
    status: deal.deal_type === 'UNCLASSIFIED' ? 'REVIEW' : 'NEW',
    deal_type: deal.deal_type,
    merchant: deal.merchant || null,
    purchase: {
      brand: deal.purchase_brand || null,
      amount: deal.purchase_amount || 0,
      currency: 'CAD'
    },
    reward: {
      type: deal.reward_type || null,
      merchant: deal.reward_merchant || null,
      amount: deal.reward_amount || 0,
      currency: 'CAD'
    },
    validity: {
      start_date: deal.validity_start_date || null,
      end_date: deal.validity_end_date || null,
      raw_text: deal.validity_raw_text || null
    },
    confidence: deal.confidence || 0,
    debug: {
      primary_score_total: primaryScore,
      candidate_score: candidateScore,
      candidate_reason_codes: candidateReasonCodes,
      ai_notes: deal.notes || []
    },
    override: {
      was_overridden: false,
      overridden_by: null,
      overridden_at: null,
      override_reason: null
    }
  }));
}

// ============================================================================
// PERSISTENCE HELPERS
// ============================================================================

export async function savePageResults(
  runId: string,
  pageNumber: number,
  imageUrl: string,
  imageStoragePath: string | null,
  primaryOcr: PrimaryOcrResult,
  candidateScoring: CandidateScoringResult,
  aiScan: AiScanResult | null
): Promise<string> {
  const pageData = {
    run_id: runId,
    page_number: pageNumber,
    image_url: imageUrl,
    image_storage_path: imageStoragePath,
    primary_ocr_engine: primaryOcr.engine,
    primary_ocr_text: primaryOcr.text,
    primary_ocr_confidence: primaryOcr.confidence,
    primary_ocr_duration_ms: primaryOcr.duration_ms,
    primary_ocr_score_total: primaryOcr.score_total,
    primary_ocr_score_breakdown: primaryOcr.score_breakdown,
    primary_ocr_error: primaryOcr.error,
    candidate_for_ai: candidateScoring.candidate_for_ai,
    candidate_score: candidateScoring.candidate_score,
    candidate_threshold: candidateScoring.threshold,
    candidate_score_breakdown: candidateScoring.score_breakdown,
    candidate_hard_block_triggered: candidateScoring.hard_block_triggered,
    candidate_reason_codes: candidateScoring.reason_codes,
    ai_status: aiScan?.status || (candidateScoring.candidate_for_ai ? 'pending' : 'skipped'),
    ai_raw_output: aiScan?.raw_output,
    ai_duration_ms: aiScan?.duration_ms,
    ai_error: aiScan?.error,
    ai_model_used: aiScan?.model_used
  };
  
  // Use insert with onConflict for proper typing
  const { data, error } = await supabase
    .from('flyer_scan_pages_v2')
    .insert(pageData as any)
    .select('id')
    .single();
  
  if (error) {
    console.error(`[CR036] Failed to save page results:`, error);
    throw error;
  }
  
  return data.id;
}

export async function saveNormalizedDeals(deals: NormalizedDeal[], pageId: string): Promise<void> {
  if (!deals.length) return;
  
  const dealsData = deals.map(deal => ({
    run_id: deal.run_id,
    page_id: pageId,
    page_number: deal.page_no,
    status: deal.status,
    deal_type: deal.deal_type,
    merchant: deal.merchant,
    purchase_brand: deal.purchase.brand,
    purchase_amount: deal.purchase.amount,
    purchase_currency: deal.purchase.currency,
    reward_type: deal.reward.type,
    reward_merchant: deal.reward.merchant,
    reward_amount: deal.reward.amount,
    reward_currency: deal.reward.currency,
    validity_start_date: deal.validity.start_date,
    validity_end_date: deal.validity.end_date,
    validity_raw_text: deal.validity.raw_text,
    confidence: deal.confidence,
    debug_primary_score_total: deal.debug.primary_score_total,
    debug_candidate_score: deal.debug.candidate_score,
    debug_candidate_reason_codes: deal.debug.candidate_reason_codes,
    debug_ai_notes: deal.debug.ai_notes
  }));
  
  const { error } = await supabase
    .from('flyer_normalized_deals')
    .insert(dealsData);
  
  if (error) {
    console.error(`[CR036] Failed to save normalized deals:`, error);
    throw error;
  }
  
  console.log(`[CR036] Saved ${deals.length} normalized deals`);
}

export async function updateRunProgress(
  runId: string,
  updates: Partial<{
    status: string;
    current_phase: string;
    pages_detected: number;
    pages_rendered: number;
    pages_ocr_completed: number;
    pages_candidate: number;
    pages_ai_scanned: number;
    deals_detected_count: number;
    completed_at: string;
    failure_code: string;
    failure_reason: string;
  }>
): Promise<void> {
  const { error } = await supabase
    .from('flyer_scan_runs_v2')
    .update(updates)
    .eq('id', runId);
  
  if (error) {
    console.error(`[CR036] Failed to update run progress:`, error);
  }
}
